import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type RefObject
} from 'react';
import { computeDropdownPlacement, type DropdownPlacement } from '../utils/popoverPositionUtils';

export interface PortalDropdownPosition extends DropdownPlacement {
  left: number;
  width: number;
}

export interface PortalDropdownOptions {
  // Fired whenever the dropdown closes — outside click or close(). Consumers
  // that are themselves mounted only while the dropdown is open (PropertySearch)
  // use it to tell their parent to unmount them.
  onClose?: () => void;
  // A height the panel never exceeds even with room to spare — a long list's
  // own cap. Placement reasons about the capped height, so a list that is
  // taller than its cap doesn't flip up when the capped panel fits below.
  maxPanelHeight?: number;
}

export interface PortalDropdown<T extends HTMLElement> {
  isOpen: boolean;
  position: PortalDropdownPosition;
  // Ready to spread onto the panel element. Consumers that want the trigger's
  // width add it themselves from `position.width` — spreading this alone is
  // what keeps a new consumer from silently losing the flip by forgetting
  // `bottom` or `maxHeight`.
  panelStyle: CSSProperties;
  triggerRef: RefObject<T | null>;
  panelRef: RefObject<HTMLDivElement | null>;
  open: () => void;
  toggle: () => void;
  close: () => void;
}

const PANEL_OFFSET = 4;
const VIEWPORT_MARGIN = 8;
const MIN_PANEL_HEIGHT = 80;
const INITIAL_POSITION: PortalDropdownPosition = { top: 0, left: 0, width: 0 };

// The panel's natural content height. Measuring the rendered box instead would
// make an already-clamped panel look like it fits, flipping it back and forth.
function readPanelHeight(panel: HTMLDivElement | null): number {
  if (!panel) return 0;
  const chrome = panel.offsetHeight - panel.clientHeight;
  return panel.scrollHeight + chrome;
}

function arePositionsEqual(a: PortalDropdownPosition, b: PortalDropdownPosition): boolean {
  return (
    a.top === b.top &&
    a.bottom === b.bottom &&
    a.left === b.left &&
    a.width === b.width &&
    a.maxHeight === b.maxHeight
  );
}

function toPanelStyle(position: PortalDropdownPosition): CSSProperties {
  return {
    position: 'fixed',
    top: position.top,
    bottom: position.bottom,
    left: position.left,
    maxHeight: position.maxHeight
  };
}

// Open/close state and fixed positioning for a dropdown panel that is
// portal'd to document.body (so ancestor overflow doesn't clip it). The panel
// is placed below the trigger and left-aligned to it, and flipped above or
// height-capped when it would otherwise run off the bottom of the viewport.
export function usePortalDropdown<T extends HTMLElement>(
  options: PortalDropdownOptions = {}
): PortalDropdown<T> {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<PortalDropdownPosition>(INITIAL_POSITION);
  const triggerRef = useRef<T>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(options.onClose);
  const observedPanelRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const isOpenRef = useRef(false);

  useEffect(() => {
    onCloseRef.current = options.onClose;
  }, [options.onClose]);

  const readPosition = useCallback((): void => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    // An open dropdown whose panel is unmounted — nothing matches the current
    // filter — has no height to measure. Placing it from a null panel would
    // stage a stale "hangs below, uncapped" position that paints the moment the
    // list comes back. While still closed the null panel is expected: that
    // first pass is what puts the panel below the trigger before it mounts.
    if (isOpenRef.current && !panelRef.current) return;
    const rect = trigger.getBoundingClientRect();
    const placement = computeDropdownPlacement({
      anchor: rect,
      panelHeight: readPanelHeight(panelRef.current),
      viewportHeight: window.innerHeight,
      anchorGap: PANEL_OFFSET,
      viewportMargin: VIEWPORT_MARGIN,
      minPanelHeight: MIN_PANEL_HEIGHT,
      maxPanelHeight: options.maxPanelHeight
    });
    const next: PortalDropdownPosition = { left: rect.left, width: rect.width, ...placement };
    // Guard against a feedback loop: re-measuring shrinks the panel, which the
    // ResizeObserver below reports as a resize, which re-measures again.
    setPosition((prev) => (arePositionsEqual(prev, next) ? prev : next));
  }, [options.maxPanelHeight]);

  const open = useCallback((): void => {
    readPosition();
    setIsOpen(true);
  }, [readPosition]);

  const toggle = useCallback((): void => {
    readPosition();
    setIsOpen((prev) => {
      if (prev) onCloseRef.current?.();
      return !prev;
    });
  }, [readPosition]);

  const close = useCallback((): void => {
    setIsOpen(false);
    onCloseRef.current?.();
  }, []);

  const readPositionRef = useRef(readPosition);

  // Runs after every render, deliberately without deps, and does three things.
  //
  // First it re-measures. The panel only exists once isOpen is true, so its
  // height — and with it the flip/clamp decision — can only be read on a second
  // pass; doing it in a layout effect keeps that off-screen. Every later render
  // needs the same treatment: leaving a filtered list's new height to the
  // ResizeObserver below would paint one frame at the old placement first, a
  // visible flash of exactly the clipping this hook exists to prevent. The
  // arePositionsEqual guard makes the extra pass free when nothing moved.
  //
  // Then it refreshes the ref the observer reads through, so a consumer whose
  // maxPanelHeight changes doesn't keep measuring against the old cap.
  //
  // Finally it (re)attaches the observer, which catches the size changes no
  // render precedes — a font swap, say. It re-attaches on node identity because
  // a panel that renders nothing unmounts and remounts while the dropdown stays
  // open, leaving the old observer watching a detached node.
  useLayoutEffect(() => {
    isOpenRef.current = isOpen;
    readPositionRef.current = readPosition;
    if (isOpen) readPosition();

    const panel = panelRef.current;
    if (panel === observedPanelRef.current) return;
    observedPanelRef.current = panel;
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!panel) return;
    const observer = new ResizeObserver(() => readPositionRef.current());
    observer.observe(panel);
    observerRef.current = observer;
  });

  useEffect(() => () => observerRef.current?.disconnect(), []);

  // Scroll is listened to in the capture phase so nested scroll containers
  // (settings tables, modal bodies) move the panel too, not just the window.
  useEffect(() => {
    if (!isOpen) return;
    window.addEventListener('resize', readPosition);
    window.addEventListener('scroll', readPosition, true);
    return () => {
      window.removeEventListener('resize', readPosition);
      window.removeEventListener('scroll', readPosition, true);
    };
  }, [isOpen, readPosition]);

  // The panel is portal'd to document.body, so a single-ref click-outside
  // (e.g. qampo's useClickOutside) treats it as "outside" and closes on every
  // option click. Both the trigger and the panel must be checked here.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent): void => {
      if (
        triggerRef.current?.contains(e.target as Node) ||
        panelRef.current?.contains(e.target as Node)
      ) {
        return;
      }
      setIsOpen(false);
      onCloseRef.current?.();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  return {
    isOpen,
    position,
    panelStyle: toPanelStyle(position),
    triggerRef,
    panelRef,
    open,
    toggle,
    close
  };
}
